import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaPlus, FaDollarSign, FaFileExport } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { MdAssignment, MdInfo, MdAttachMoney, MdPending, MdCheckCircle, MdClear } from "react-icons/md";
import { 
  Button, 
  ButtonGroup, 
  Badge, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Typography,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  Switch,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { BillingUtils } from '../Services/billingService';

// TODO: Crear componente UniversalSearchGeneral
// Ubicación: src/components/BillableActivities/Buttons/UniversalSearchGeneral.js
// Props esperadas: { onSearch, placeholder }
// Propósito: Barra de búsqueda universal específica para actividades generales
import UniversalSearchGeneral from '../Buttons/UniversalSearch';

// TODO: Crear componente BillingActionButtonGeneral
// Ubicación: src/components/BillableActivities/Buttons/BillingActionButtonGeneral.js  
// Props esperadas: { selectedActivities, onCreateBillableActivities, currentUserRoles, isProcessing }
// Propósito: Botón con acciones masivas específicas para actividades generales
import BillingActionButtonGeneral from '../Buttons/BillingActionButtonGeneral';

const BillingFiltersHeaderGeneral = ({ 
  filters, 
  onFiltersChange, 
  onSearchChange,
  selectedActivities,
  onCreateBillableActivities,
  onReloadData,
  onClearSelection,
  isLoading,
  statsData, // NUEVO: datos de estadísticas por estado
  currentUserRoles
}) => {
  // Estado para los filtros activos (que tienen valores)
  const [activeFilters, setActiveFilters] = useState([]);
  
  // Estado para mostrar/ocultar la sección de filtros activos
  const [showActiveFilters, setShowActiveFilters] = useState(true);
  
  // Estado para mostrar/ocultar el offcanvas de filtros
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);

  // Estados para diálogos de acciones masivas
  const [showBulkActionsDialog, setShowBulkActionsDialog] = useState(false);

  // Refs
  const offcanvasRef = useRef(null);
  const filterButtonRef = useRef(null);

  // Opciones para tipos de actividades (específicas para esta tabla)
  const activityTypeOptions = [
    { value: 'appointments', label: 'Citas/Consultas' },
    { value: 'prescriptions', label: 'Recetas Tradicionales' },
    { value: 'electronic_prescriptions', label: 'Recetas Electrónicas' },
    { value: 'treatments', label: 'Tratamientos' },
    { value: 'laboratory', label: 'Análisis de Laboratorio' },
    { value: 'imaging', label: 'Diagnóstico por Imagen' },
    { value: 'molecular', label: 'Medicina Molecular' },
    { value: 'vaccinations', label: 'Vacunaciones' }
  ];

  // NUEVO: Configuración de estados de facturación disponibles
  const billingStatusOptions = [
    { value: '', label: 'Todos los Estados' },
    { value: 'available_for_billing', label: 'Disponible para Facturar', color: 'primary' },
    { value: 'billable_created_pending', label: 'AF Creada (Pendiente)', color: 'warning' },
    { value: 'billable_created_ready', label: 'AF Lista', color: 'success' },
    { value: 'sales_order_created', label: 'Orden de Venta Creada', color: 'secondary' },
    { value: 'partially_billed', label: 'Parcialmente Facturada', color: 'info' },
    { value: 'fully_billed', label: 'Totalmente Facturada', color: 'success' },
    { value: 'marked_non_billable', label: 'Marcada No Facturable', color: 'error' }
  ];

  // Opciones para estados de facturación (mantenemos compatibilidad)
  const statusOptions = [
    { value: 'no_facturada', label: 'No Facturada' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'ready', label: 'Lista' },
    { value: 'facturada', label: 'Facturada' },
    { value: 'parcialmente_facturada', label: 'Parcialmente Facturada' },
    { value: 'no_facturable', label: 'No Facturable' }
  ];

  // Helper para obtener color del estado
  const getStatusColor = (color) => {
    const colorMap = {
      'primary': '#2196f3',
      'warning': '#ff9800',
      'success': '#4caf50',
      'secondary': '#9c27b0',
      'info': '#2196f3',
      'error': '#f44336'
    };
    return colorMap[color] || '#999';
  };

  // Función para convertir objeto de filtros a array para mostrar
  const convertFiltersToArray = (filtersObj) => {
    const filterLabels = {
      search: 'Búsqueda General',
      patient_search: 'Búsqueda Paciente',
      doctor_id: 'ID Médico',
      center_id: 'ID Centro',
      date_from: 'Fecha Desde',
      date_to: 'Fecha Hasta',
      include_types: 'Tipos de Actividad',
      only_unbilled: 'Solo No Facturadas',
      activity_type: 'Tipo Específico',
      status: 'Estado',
      billing_status: 'Estado de Facturación', // NUEVO
      only_without_af: 'Solo sin AF', // NUEVO
      only_with_af: 'Solo con AF', // NUEVO
      min_amount: 'Importe Mínimo',
      max_amount: 'Importe Máximo'
    };

    return Object.entries(filtersObj)
      .filter(([key, value]) => {
        if (key === 'include_types') {
          return Array.isArray(value) && value.length > 0 && value.length < activityTypeOptions.length;
        }
        if (key === 'only_unbilled' || key === 'only_without_af' || key === 'only_with_af') {
          return value === true;
        }
        return value !== '' && value !== null && value !== undefined && value !== false;
      })
      .map(([key, value]) => {
        let displayValue = value;
        
        if (key === 'include_types' && Array.isArray(value)) {
          displayValue = `${value.length} tipos seleccionados`;
        } else if (key === 'only_unbilled' || key === 'only_without_af' || key === 'only_with_af') {
          displayValue = 'Sí';
        } else if (key === 'status') {
          const statusOption = statusOptions.find(opt => opt.value === value);
          displayValue = statusOption ? statusOption.label : value;
        } else if (key === 'billing_status') {
          const billingOption = billingStatusOptions.find(opt => opt.value === value);
          displayValue = billingOption ? billingOption.label : value;
        }
        
        return { 
          label: filterLabels[key] || key.charAt(0).toUpperCase() + key.slice(1),
          value: displayValue,
          key 
        };
      });
  };

  // Helper functions para llamadas seguras
  const safeCallOnFiltersChange = (newFilters) => {
    if (onFiltersChange && typeof onFiltersChange === 'function') {
      onFiltersChange(newFilters);
    }
  };

  const safeCallOnSearchChange = (searchTerm) => {
    if (onSearchChange && typeof onSearchChange === 'function') {
      onSearchChange(searchTerm);
    }
  };

  const safeCallOnReloadData = () => {
    if (onReloadData && typeof onReloadData === 'function') {
      onReloadData();
    }
  };

  const safeCallOnClearSelection = () => {
    if (onClearSelection && typeof onClearSelection === 'function') {
      onClearSelection();
    }
  };

  // Actualizar filtros activos cuando cambian los filtros del padre
  useEffect(() => {
    const newActiveFilters = convertFiltersToArray(filters);
    setActiveFilters(newActiveFilters);
  }, [filters]);

  // Maneja cambios en los filtros
  const handleFilterChange = (newFilters) => {
    // Notifica al componente padre
    safeCallOnFiltersChange(newFilters);
  };

  // Maneja la búsqueda universal
  const handleUniversalSearch = (searchTerm) => {
    const updatedFilters = { 
      ...filters, 
      search: searchTerm 
    };
    
    // Notifica al componente padre
    safeCallOnSearchChange(searchTerm);
    safeCallOnFiltersChange(updatedFilters);
  };

  // Remueve un filtro específico
  const removeFilter = (filterToRemove) => {
    const updatedFilters = { ...filters };
    
    // Limpia el valor del filtro
    if (filterToRemove.key === 'include_types') {
      updatedFilters[filterToRemove.key] = activityTypeOptions.map(opt => opt.value);
    } else if (filterToRemove.key === 'only_unbilled' || filterToRemove.key === 'only_without_af' || filterToRemove.key === 'only_with_af') {
      updatedFilters[filterToRemove.key] = false;
    } else {
      updatedFilters[filterToRemove.key] = '';
    }
    
    // Notifica al componente padre
    safeCallOnFiltersChange(updatedFilters);
  };

  // NUEVO: Limpia todos los filtros (versión mejorada)
  const clearAllFilters = () => {
    const resetFilters = {
      billing_status: '',
      only_without_af: false,
      only_with_af: false,
      search: '',
      patient_search: '',
      date_from: '',
      date_to: '',
      doctor_id: '',
      center_id: '',
      include_types: activityTypeOptions.map(opt => opt.value), // Incluir TODOS los tipos por defecto
      only_unbilled: false,
      activity_type: '',
      status: '',
      min_amount: '',
      max_amount: ''
    };

    // SOLO notificar al padre sobre los filtros - NO llamar a reloadData
    safeCallOnFiltersChange(resetFilters);
    
    // Limpia las selecciones si existe la función
    safeCallOnClearSelection();
  };

  // NUEVO: Manejadores para filtros rápidos
  const handleQuickFilter = (filterType, value) => {
    const currentValue = filters[filterType];
    const newValue = currentValue === value ? '' : value;
    
    handleFilterChange({
      ...filters,
      [filterType]: newValue
    });
  };

  // Toggle para mostrar/ocultar filtros activos
  const toggleActiveFiltersVisibility = () => {
    setShowActiveFilters(!showActiveFilters);
  };

  // Toggle para mostrar/ocultar offcanvas de filtros
  const toggleFilterOffcanvas = () => {
    setShowFilterOffcanvas(!showFilterOffcanvas);
  };

  // Cerrar offcanvas
  const closeFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  // Maneja acciones masivas
  const handleCreateBillableActivities = () => {
    if (selectedActivities.length === 0) {
      alert('Please select activities to create billable activities');
      return;
    }
    
    if (onCreateBillableActivities && typeof onCreateBillableActivities === 'function') {
      onCreateBillableActivities(selectedActivities);
    }
    setShowBulkActionsDialog(false);
  };

  // Combina filtros activos con actividades seleccionadas
  const allActiveFilters = [
    ...activeFilters,
    ...(selectedActivities.length > 0 
      ? [{ 
          label: 'Selected Activities', 
          value: selectedActivities.length, 
          key: 'selectedActivities' 
        }] 
      : [])
  ];

  // Verificar permisos (específicos para esta tabla)
  const canCreateBillableActivities = BillingUtils?.canCreateInvoices?.(currentUserRoles) || false;

  // Efecto para manejar clic fuera del offcanvas
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showFilterOffcanvas) return;

      const target = event.target;
      
      const isClickInsideOffcanvas = offcanvasRef.current && 
        offcanvasRef.current.contains(target);
      
      const isClickOnFilterButton = filterButtonRef.current && 
        filterButtonRef.current.contains(target);
      
      if (!isClickInsideOffcanvas && !isClickOnFilterButton) {
        setShowFilterOffcanvas(false);
      }
    };

    if (showFilterOffcanvas) {
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFilterOffcanvas]);

  // Efecto para manejar tecla Escape
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showFilterOffcanvas) {
          setShowFilterOffcanvas(false);
        }
        if (showBulkActionsDialog) {
          setShowBulkActionsDialog(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showFilterOffcanvas, showBulkActionsDialog]);

  // Efecto para controlar overflow del body
  useEffect(() => {
    if (showFilterOffcanvas) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showFilterOffcanvas]);

  return (
    <>     
      {/* Header principal con búsqueda y botones */}
      <div className="filter-container py-3">
        <div className="row align-items-center">
          <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
            <UniversalSearchGeneral 
              onSearch={handleUniversalSearch}
              placeholder="Search activities across all patients..."
            />
          </div>

          {/* Botones de acción */}
          <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 gy-3 flex-wrap">
            {/* Botones de acción masiva */}
            
              <BillingActionButtonGeneral
                selectedActivities={selectedActivities}
                onCreateBillableActivities={onCreateBillableActivities}
                refreshData={onReloadData}  
                currentUserRoles={currentUserRoles}
                isProcessingBulkAction={isLoading}  
              />
            

            {/* Botones de filtros */}
            <Link 
                            to="#" 
                            className="btn btn-gray filterbtn d-flex align-items-center gap-1 position-relative"
                            type="button"
                onClick={toggleFilterOffcanvas}
              >
                <div className="filter_text d-flex align-items-center gap-1">
                                                                        <span className="d-block material-symbols-outlined icon-md">tune</span>
                                                                        <span className="d-block">Filter</span>
                                                                    </div>
                                {/* Show badge only if there are active filters */}
                                {allActiveFilters.length > 0 && (
                                  <span 
                                    className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-primary" 
                                    style={{fontSize: '0.6rem', padding: '0.2rem 0.4rem'}}
                                  >
                                    {allActiveFilters.length}
                                  </span>
                                )}
                              </Link>

            {/* Botón para recargar datos */}
            <button
              className="btn btn-primary addnewbtn d-flex align-items-center gap-1"
              type="button"
              onClick={safeCallOnReloadData}
              disabled={isLoading}
            >
            <span className="d-block material-symbols-outlined icon-md">refresh</span>
                                        <span className="d-block">Refresh</span>
      </button>
          </div>
        </div>
      </div>

      {/* Backdrop para el offcanvas */}
      {showFilterOffcanvas && (
        <div 
          className="offcanvas-backdrop fade show"
          onClick={closeFilterOffcanvas}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1040,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
        />
      )}

      {/* Offcanvas de filtros */}
      <div 
        className={`offcanvas offcanvas-end ${showFilterOffcanvas ? 'show' : ''}`}
        tabIndex="-1" 
        id="filtersOffcanvasGeneral"
        ref={offcanvasRef}
        style={{
          visibility: showFilterOffcanvas ? 'visible' : 'hidden',
          width: '450px',
          transform: showFilterOffcanvas ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: 1045,
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          backgroundColor: 'white'
        }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title">Patient Activities Filters</h5>
          <button 
            type="button" 
            className="btn-close"
            onClick={closeFilterOffcanvas}
            aria-label="Close"
          ></button>
        </div>
        <div className="offcanvas-body p-0" style={{ overflowY: 'auto' }}>
          <div className="p-3">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* Búsqueda de pacientes */}
              <TextField
                fullWidth
                label="Search Patient"
                placeholder="Patient name or ID"
                value={filters.patient_search || ''}
                onChange={(e) => handleFilterChange({
                  ...filters, 
                  patient_search: e.target.value 
                })}
                size="small"
              />

              {/* NUEVO: Estado de Facturación */}
              <FormControl fullWidth size="small">
                <InputLabel>Estado de Facturación</InputLabel>
                <Select
                  value={filters.billing_status || ''}
                  onChange={(e) => handleFilterChange({
                    ...filters, 
                    billing_status: e.target.value 
                  })}
                  label="Estado de Facturación"
                >
                  {billingStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.color && option.value && (
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: getStatusColor(option.color)
                            }}
                          />
                        )}
                        <Typography variant="body2">{option.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* NUEVO: Switches para filtros AF */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.only_without_af || false}
                      onChange={(e) => handleFilterChange({
                        ...filters,
                        only_without_af: e.target.checked,
                        only_with_af: e.target.checked ? false : filters.only_with_af // Mutuamente exclusivos
                      })}
                      color="primary"
                    />
                  }
                  label="Solo sin AF creada"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.only_with_af || false}
                      onChange={(e) => handleFilterChange({
                        ...filters,
                        only_with_af: e.target.checked,
                        only_without_af: e.target.checked ? false : filters.only_without_af // Mutuamente exclusivos
                      })}
                      color="success"
                    />
                  }
                  label="Solo con AF creada"
                />
              </Box>

              {/* Filtros de fecha */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From Date"
                    value={filters.date_from || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters, 
                      date_from: e.target.value 
                    })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To Date"
                    value={filters.date_to || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters, 
                      date_to: e.target.value 
                    })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Filtros de IDs */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Doctor ID"
                    placeholder="Doctor ID"
                    value={filters.doctor_id || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters, 
                      doctor_id: e.target.value 
                    })}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Center ID"
                    placeholder="Center ID"
                    value={filters.center_id || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters, 
                      center_id: e.target.value 
                    })}
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Tipos de actividades */}
              <FormControl fullWidth size="small">
                <InputLabel>Activity Types</InputLabel>
                <Select
                  multiple
                  value={filters.include_types || activityTypeOptions.map(opt => opt.value)}
                  onChange={(e) => handleFilterChange({
                    ...filters, 
                    include_types: e.target.value 
                  })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.slice(0, 2).map((value) => {
                        const option = activityTypeOptions.find(opt => opt.value === value);
                        return (
                          <Chip 
                            key={value} 
                            label={option?.label || value} 
                            size="small" 
                          />
                        );
                      })}
                      {selected.length > 2 && (
                        <Chip 
                          label={`+${selected.length - 2} more`} 
                          size="small" 
                          color="primary"
                        />
                      )}
                    </Box>
                  )}
                >
                  {activityTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Estado de facturación (legacy) */}
              <FormControl fullWidth size="small">
                <InputLabel>Billing Status (Legacy)</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange({
                    ...filters, 
                    status: e.target.value 
                  })}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filtros de importe */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Amount"
                    placeholder="0.00"
                    value={filters.min_amount || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters, 
                      min_amount: e.target.value 
                    })}
                    size="small"
                    InputProps={{ startAdornment: '€' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Amount"
                    placeholder="999999.99"
                    value={filters.max_amount || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters, 
                      max_amount: e.target.value 
                    })}
                    size="small"
                    InputProps={{ startAdornment: '€' }}
                  />
                </Grid>
              </Grid>

              {/* Checkbox para solo no facturadas */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.only_unbilled || false}
                    onChange={(e) => handleFilterChange({
                      ...filters, 
                      only_unbilled: e.target.checked 
                    })}
                    color="primary"
                  />
                }
                label="Show only unbilled activities"
              />

              {/* Botones de acción del filtro */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={clearAllFilters}
                  size="small"
                  fullWidth
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={closeFilterOffcanvas}
                  size="small"
                  fullWidth
                >
                  Apply Filters
                </Button>
              </Box>
              
            </Box>
          </div>
        </div>
      </div>

      {/* Dialog de acciones masivas */}
      <Dialog 
        open={showBulkActionsDialog} 
        onClose={() => setShowBulkActionsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Actions ({selectedActivities.length} activities)</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {canCreateBillableActivities && (
              <Button
                variant="outlined"
                startIcon={<MdAssignment />}
                onClick={handleCreateBillableActivities}
                disabled={isLoading}
                fullWidth
              >
                Create Billable Activities
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<FaFileExport />}
              onClick={() => {
                // TODO: Implementar exportación
                console.log('Export selected activities:', selectedActivities);
                setShowBulkActionsDialog(false);
              }}
              disabled={isLoading}
              fullWidth
            >
              Export Selected Activities
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkActionsDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sección de filtros activos */}
      {showActiveFilters && allActiveFilters.length > 0 && (
        <div className="container-fluid mt-2">
          <div className="row">
            <div className="col-12">
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                flexWrap: 'wrap',
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Active Filters:
                </Typography>
                
                {/* Renderiza cada filtro activo como chip */}
                {allActiveFilters.map((filter, index) => (
                  <Chip
                    key={index}
                    label={`${filter.label}: ${filter.value}`}
                    onDelete={() => {
                      if (filter.key === 'selectedActivities') {
                        safeCallOnClearSelection();
                      } else {
                        removeFilter(filter);
                      }
                    }}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
                
                {/* Botón para limpiar todos los filtros */}
                {allActiveFilters.length > 0 && (
                  <Button 
                    size="small"
                    onClick={clearAllFilters}
                    sx={{ ml: 'auto' }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BillingFiltersHeaderGeneral;